import React, { useEffect, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';
import { type NativePrintProps, NativeReportShell } from '~/nativeLenses/NativePrintBoard';
import { nativeLensId } from '~/nativeLenses/nativeLensId';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Popover,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';
import type { PageField } from '~/pages/AskRichardUnified/page/pageContract';
import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';

import { useGetReportingLeLensData } from '~/hooks/apis/reporting/useGetReportingLeLensData';

import { csvFileName } from '../csvValues';

import FidelityChart from './FidelityChart';
import ProductMeasures from './ProductMeasures';
import RecoveredWorksheets, { type WorksheetContext } from './RecoveredWorksheets';
import {
  adaptComms,
  countryGroupLabel,
  downloadCommsCsv,
  formatSourceDate,
  reportCardFailed,
  requestComms,
  requestProblem,
  requestedCards,
  viewKeys,
} from './liveDataAdapter';
import { reportPalette } from './palette';
import {
  CADENCES,
  MEASURES,
  Measure,
  REPORT_VIEWS,
  Report,
  ReportState,
  SPLITS,
  TableRecord,
  editReportParams,
  metricFormat,
  readReport,
  reportParams,
  selection,
  selectionValue,
  syncReportParams,
  validation,
} from './presentationModel';
import config from './reportConfig.json';
import {
  ALL_VIEW_FILTER_KEYS,
  SHEET_KEYS,
  VIEW_FILTERS,
  isWorksheetDateView,
  sheetRange,
  viewFilterValues,
  withViewFilter,
} from './viewFilters';
import { tabWorksheetRows, worksheetCsv } from './worksheetModel';

const PAGE_SIZE = 200;
const workbookUrl = 'https://10ay.online.tableau.com/#/site/luxuryescapescomau/workbooks/1370641';

const chartSets: Record<string, Array<Measure>> = {
  'Charts: Comms Metrics': ['sends', 'clickRate', 'openRate', 'unsubRate'],
  'Charts: Financial Metrics': ['ttv', 'gm', 'valuePerSend', 'purchaseRate'],
  'Charts: Health Metrics': ['sessionRatio'],
};
const unavailableReason = 'Unavailable: a matching live source binding has not been recovered.';

const SEND_VIEWS = ['tables', 'comms', 'financial', 'health'];
function downloadText(name: string, content: string) {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' })),
    link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
type ControlProps = {
  label: string;
  value?: string;
  values?: ReadonlyArray<string>;
  onChange?: (value: string) => void;
  disabled?: boolean;
  date?: boolean;
};
function CompactControl({ label, value = 'Unavailable', values, onChange, disabled, date }: ControlProps) {
  return (
    <Box sx={{ minWidth: 0 }} title={disabled ? unavailableReason : value}>
      <Typography
        component="label"
        variant="caption"
        sx={{ display: 'block', fontSize: 10, lineHeight: 1.5, overflowWrap: 'anywhere' }}
      >
        {label}
        {disabled ? ' ⓘ' : ''}
      </Typography>
      <TextField
        select={Boolean(values)}
        value={value}
        type={date ? 'date' : 'text'}
        disabled={disabled}
        size="small"
        fullWidth
        onChange={(event) => onChange?.(event.target.value)}
        slotProps={{ htmlInput: { 'aria-label': label }, select: { inputProps: { 'aria-label': label } } }}
        sx={{
          '& .MuiInputBase-root': { height: 28, fontSize: 11 },
          '& .MuiInputBase-input': { px: 0.75, py: 0.5 },
          '& .MuiSelect-select': { pr: 3 },
        }}
      >
        {values?.map((item) => (
          <MenuItem key={item} value={item} sx={{ fontSize: 12 }}>
            {item}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
}
function MultiControl({
  label,
  value,
  values,
  onApply,
}: {
  label: string;
  value: string;
  values: Array<string>;
  onApply: (value: string) => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<Array<string>>([]);
  const [search, setSearch] = useState('');
  const active = selection(value);
  // Dropdown values are capped at 500 per filter and come from the current window, so keep every selected
  // value listed and let a typed value be added.
  const listed = [...new Set([...active, ...draft, ...values])];
  const typed = search.trim();
  function open(event: React.MouseEvent<HTMLElement>) {
    setAnchor(event.currentTarget);
    setDraft(active);
    setSearch('');
  }
  function toggle(item: string) {
    setDraft((previous) =>
      previous.includes(item) ? previous.filter((member) => member !== item) : [...previous, item],
    );
  }
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ display: 'block', fontSize: 10, lineHeight: 1.5 }}>
        {label}
      </Typography>
      <Button
        variant="outlined"
        onClick={open}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
        sx={{
          width: '100%',
          minWidth: 0,
          height: 28,
          borderRadius: 0.5,
          px: 0.75,
          py: 0,
          fontSize: 11,
          justifyContent: 'space-between',
        }}
      >
        <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {active.length === 0 ? 'All' : active.length === 1 ? active[0] : `${active.length} selected`}
        </Box>
        <Box component="span" aria-hidden="true">
          ▾
        </Box>
      </Button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box role="dialog" aria-label={`${label} selections`} sx={{ width: 280, maxWidth: 'calc(100vw - 24px)', p: 1 }}>
          <Typography variant="caption">{label} · apply selections</Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="Find a value"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            slotProps={{ htmlInput: { 'aria-label': `Search ${label}` } }}
            sx={{ my: 0.75 }}
          />
          <FormControlLabel
            control={<Checkbox size="small" checked={draft.length === 0} onChange={() => setDraft([])} />}
            label="All values"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: 12 } }}
          />
          <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
            {/* Matching is case-sensitive, so a case variant of a listed value is not offered as a new one. */}
            {typed && !listed.some((item) => item.toLowerCase() === typed.toLowerCase()) && (
              <FormControlLabel
                control={<Checkbox size="small" checked={false} onChange={() => toggle(typed)} />}
                label={`Add "${typed}"`}
                sx={{
                  display: 'flex',
                  m: 0,
                  '& .MuiFormControlLabel-label': { fontSize: 12, overflowWrap: 'anywhere' },
                }}
              />
            )}
            {listed
              .filter((item) => item.toLowerCase().includes(search.toLowerCase()))
              .map((item) => (
                <FormControlLabel
                  key={item}
                  control={<Checkbox size="small" checked={draft.includes(item)} onChange={() => toggle(item)} />}
                  label={item}
                  sx={{
                    display: 'flex',
                    m: 0,
                    '& .MuiFormControlLabel-label': { fontSize: 12, overflowWrap: 'anywhere' },
                  }}
                />
              ))}
          </Box>
          <Stack direction="row" justifyContent="flex-end">
            <Button size="small" onClick={() => setAnchor(null)}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                onApply(selectionValue(draft));
                setAnchor(null);
              }}
            >
              Apply
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
}
function FilterGroup({ title, columns, children }: { title: string; columns: number; children: React.ReactNode }) {
  return (
    <Box
      component="fieldset"
      sx={{ minWidth: 0, m: 0, px: 0.75, pb: 0.75, pt: 0.25, border: 1, borderColor: 'divider', borderRadius: 0.5 }}
    >
      <Typography component="legend" variant="caption" fontWeight={600} sx={{ px: 0.5, fontSize: 11 }}>
        {title}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: `repeat(${columns}, minmax(0, 1fr))` },
          gap: 0.75,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
function MetricChart({ report, measure }: { report: Report; measure: Measure }) {
  const name = MEASURES.find((item) => item.key === measure).label;
  return (
    <Box sx={{ minWidth: 0, bgcolor: reportPalette.white, color: reportPalette.black, p: 1 }}>
      <Typography component="h2" variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
        {name}
      </Typography>
      <FidelityChart report={report} measure={measure} height={350} />
      <Typography variant="caption" sx={{ fontSize: 10, display: 'block', mt: 0.75 }}>
        comms_reporting_send_level_jdbc · {report.state.filters.from} to {report.state.filters.to}
      </Typography>
    </Box>
  );
}
function ClickDateChart({ report }: { report: Report }) {
  return (
    <Box sx={{ minWidth: 0, bgcolor: reportPalette.white, color: reportPalette.black, p: 1 }}>
      <Typography component="h2" variant="body2">
        Clicks By Date · {report.clickDimension}
      </Typography>
      <FidelityChart report={report} stacked height={350} />
      <Typography variant="caption" sx={{ fontSize: 10, display: 'block', mt: 0.75 }}>
        comms_reporting_click_level · {report.state.clickFrom} to {report.state.clickTo} · items and same-offer sales
        use a separate grain
      </Typography>
    </Box>
  );
}

export function NativeCustomerCommsContent() {
  const [now] = useState(() => new Date());
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState<ReportState>(() => readReport(params, now));
  const [countryDomain, setCountryDomain] = useState<Array<string> | null>(null);
  const [worksheetContext, setWorksheetContext] = useState<WorksheetContext>();
  const [methodOpen, setMethodOpen] = useState(false);
  const [unavailableOpen, setUnavailableOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<{ key: 'split' | 'time' | Measure; direction: 'asc' | 'desc' } | null>(null);
  const request = useMemo(() => requestComms(state, params, now, countryDomain), [state, params, now, countryDomain]);
  const constraint = requestProblem(request, params);
  const datesValid =
    !validation(state.filters) && !validation({ ...state.filters, from: state.clickFrom, to: state.clickTo });
  const query = useGetReportingLeLensData(
    datesValid && !constraint ? nativeLensId('customer-comms') : undefined,
    request,
    true,
    undefined,
    undefined,
    undefined,
    requestedCards(state),
  );
  const sourceCountryDomain = query.data?.filterOptions?.subscriber_country_group;
  const domainKey = JSON.stringify(sourceCountryDomain);
  useEffect(() => {
    if (sourceCountryDomain && JSON.stringify(countryDomain) !== domainKey) {
      setCountryDomain(sourceCountryDomain);
    }
  }, [domainKey, sourceCountryDomain, countryDomain]);
  const domainReady =
    !selection(state.filters.market).length || (countryDomain !== null && JSON.stringify(countryDomain) === domainKey);
  const modelConstraint =
    constraint ?? (!domainReady ? 'Waiting for live Country Group members for this selection.' : null);
  const settled = useMemo(
    () =>
      adaptComms(
        state,
        {
          requestIdentity: query.requestIdentity,
          responseIdentity: query.responseIdentity,
          isSuccess: query.isSuccess,
          isFetching: query.isFetching,
          isError: query.isError,
          isShowingPreviousData: query.isShowingPreviousData,
          cards: query.data?.cards,
        },
        request,
        modelConstraint,
      ),
    [
      state,
      query.requestIdentity,
      query.responseIdentity,
      query.isSuccess,
      query.isFetching,
      query.isError,
      query.isShowingPreviousData,
      query.data?.cards,
      request,
      modelConstraint,
    ],
  );
  const report = settled.report;
  const viewKey = viewKeys[state.view];
  const sheetDates = isWorksheetDateView(viewKey);
  const sheet = sheetRange(viewKey, state, now);
  const shownFrom = sheetDates ? sheet.from : state.filters.from;
  const shownTo = sheetDates ? sheet.to : state.filters.to;
  const viewFilters = VIEW_FILTERS[viewKey];
  const cardFailed = !query.isFetching && reportCardFailed(state, settled.cards);
  const filterOptions = {
    channel: query.data?.filterOptions?.utm_medium ?? [],
    market: [...new Set((sourceCountryDomain ?? []).map(countryGroupLabel))].sort(),
    campaign: query.data?.filterOptions?.utm_campaign ?? [],
    product: query.data?.filterOptions?.product_name ?? [],
  };
  const productOptions = ['All', ...filterOptions.product];
  const worksheetIdentity = report.identity + query.requestIdentity + params.toString();
  const hasWorksheets = ['clicks', 'regions', 'offers', 'vendors'].includes(viewKey);
  const bridgeReady =
    settled.ready && (!hasWorksheets || (worksheetContext?.identity === worksheetIdentity && worksheetContext.ready));
  const schema: Record<string, PageField> = {
    view: { type: 'enum', values: REPORT_VIEWS },
    cadence: { type: 'enum', values: CADENCES },
    split: { type: 'enum', values: SPLITS },
    timeSplit: { type: 'enum', values: ['Date Cadence', 'Full Period'] },
    from: { type: 'date' },
    to: { type: 'date' },
    clickFrom: { type: 'date' },
    clickTo: { type: 'date' },
    mode: { type: 'enum', values: ['Unique', 'Total'] },
    channel: { type: 'enum', values: ['All', ...filterOptions.channel] },
    market: { type: 'enum', values: ['All', ...filterOptions.market] },
    campaign: { type: 'enum', values: ['All', ...filterOptions.campaign] },
    product: { type: 'enum', values: productOptions },
  };
  const isClicks = state.view === 'Clicks Insights';
  const isTable = state.view === REPORT_VIEWS[0];
  const chartMeasures = chartSets[state.view];
  const csvAvailable = settled.ready;
  const csvUnavailableReason = query.error?.message ?? settled.problem ?? 'Waiting for live data.';
  function downloadCsv() {
    if (!settled.ready) {
      return;
    }
    if (isTable || chartMeasures || isClicks) {
      downloadCommsCsv(report);
      return;
    }
    // Strategic Regions, Offer Clicks and Offer Vendor: every worksheet row on the tab, as Tableau downloads them.
    downloadText(
      csvFileName('customer-comms-' + viewKey, shownFrom, shownTo),
      worksheetCsv(tabWorksheetRows(settled.worksheets, viewKey), state.filters.mode === 'Unique', [
        'Scope',
        state.view,
        'AUD',
        'sourceAsOf=' + (report.sourceAsOf ?? 'Unknown'),
        // The worksheet rows use the tab's own send dates (sheet_from to sheet_to in the request below).
        'Send dates=' + shownFrom + ' to ' + shownTo,
        'effectiveRequest=' + JSON.stringify(settled.scope),
      ]),
    );
  }
  const error = validation(state.filters);
  const clickError = validation({ ...state.filters, from: state.clickFrom, to: state.clickTo });
  const rows = useMemo(() => {
    if (!sort) {
      return report.tableRows;
    }
    return [...report.tableRows].sort((a, b) => {
      const left = sort.key === 'split' || sort.key === 'time' ? a[sort.key] : a.values[sort.key];
      const right = sort.key === 'split' || sort.key === 'time' ? b[sort.key] : b.values[sort.key];
      if (left == null) {
        return right == null ? 0 : 1;
      }
      if (right == null) {
        return -1;
      }
      const order =
        typeof left === 'number' && typeof right === 'number'
          ? left - right
          : String(left).localeCompare(String(right));
      return order * (sort.direction === 'asc' ? 1 : -1);
    });
  }, [report.tableRows, sort]);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pages - 1);
  const shownRows = rows.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
  usePageBridge({
    pageId: 'customer-comms',
    view: {
      title: `Customer Comms Key Report · ${state.view}`,
      ready: bridgeReady,
      filters: {
        ...state.filters,
        view: state.view,
        cadence: state.cadence,
        split: state.split,
        timeSplit: state.timeSplit,
        clickFrom: state.clickFrom,
        clickTo: state.clickTo,
        product: state.product,
      },
      filterSchema: schema,
      sourceAsOf: report.sourceAsOf,
      effectiveRequest: request,
      summary: report.totals,
      clickTotal: report.clickTotal,
      clickSummaryDefinition:
        'Independent clicks_summary population; custom message input and saved worksheet-only restrictions do not apply to that separate source.',
      worksheetPopulation: settled.worksheets.length,
      visibleSections: hasWorksheets ? worksheetContext?.sections : shownRows.slice(0, 25),
      visibleRowsAreSample: !hasWorksheets,
      fullTableRowCount: rows.length,
      summarySource: 'sends_summary',
      selectedMark: hasWorksheets ? worksheetContext?.selections : null,
      worksheetMetric: worksheetContext?.metric,
    },
    edit: (changes) => {
      if (!bridgeReady) {
        throw new Error('Customer Comms data is not ready.');
      }
      validatePageFilters(changes, schema);
      const next = reportParams(state);
      for (const [key, value] of Object.entries(changes)) {
        next.set(key === 'view' ? 'commsView' : key, String(value));
      }
      const retained = new URLSearchParams(params);
      const aliases: Record<string, string> = {
        channel: 'utm_medium',
        market: 'subscriber_country_group',
        campaign: 'utm_campaign',
        product: 'product_name',
      };
      for (const key of Object.keys(changes)) {
        if (aliases[key]) {
          retained.delete(aliases[key]);
        }
      }
      setParams(retained, { replace: true });
      setState(readReport(next, now));
      setPage(0);
    },
  });

  useEffect(() => {
    if (constraint) {
      return;
    }
    const next = syncReportParams(params, state, now);
    if (next.toString() !== params.toString()) {
      setParams(next, { replace: true });
    }
  }, [state, params, setParams, constraint]);
  useEffect(() => {
    const restore = () => {
      setState(readReport(new URLSearchParams(window.location.search), now));
      setPage(0);
    };
    window.addEventListener('popstate', restore);
    return () => window.removeEventListener('popstate', restore);
  }, []);
  function update(next: Partial<ReportState>) {
    const edit = editReportParams(params, state, next);
    setState(edit.state);
    setParams(edit.params, { replace: true });
    setPage(0);
  }
  function filter(key: keyof ReportState['filters'], value: string) {
    const aliases: Record<string, string> = {
      channel: 'utm_medium',
      market: 'subscriber_country_group',
      campaign: 'utm_campaign',
    };
    if (aliases[key]) {
      const next = new URLSearchParams(params);
      next.delete(aliases[key]);
      setParams(next, { replace: true });
    }
    setState((previous) => ({ ...previous, filters: { ...previous.filters, [key]: value } }));
    setPage(0);
  }
  function nativeParameter(key: string, value: string) {
    const next = new URLSearchParams(params);
    next.set(key, value);
    setParams(next, { replace: true });
    setPage(0);
  }
  function viewFilter(key: string, value: string) {
    setParams(withViewFilter(params, viewKey, key, selection(value)), { replace: true });
    setPage(0);
  }
  function sortBy(key: 'split' | 'time' | Measure) {
    setSort((previous) => ({
      key,
      direction: previous?.key === key && previous.direction === 'desc' ? 'asc' : 'desc',
    }));
    setPage(0);
  }
  function tableRow(row: TableRecord, total = false, noMatchingRows = false) {
    return (
      <TableRow
        key={row.id}
        hover={!total}
        sx={{ bgcolor: total ? 'action.selected' : undefined, '& td': { fontWeight: total ? 600 : 400 } }}
      >
        <TableCell component="th" scope="row">
          {row.split}
        </TableCell>
        <TableCell>{row.time}</TableCell>
        {MEASURES.map((measure) => (
          <TableCell key={measure.key} align="right">
            {metricFormat(row.values[measure.key], measure.key, noMatchingRows)}
          </TableCell>
        ))}
      </TableRow>
    );
  }

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        minWidth: 0,
        display: 'block',
        p: { xs: 1, sm: 1.5 },
        '& > * + *': { mt: 1 },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'start', sm: 'center' }}
        gap={0.5}
        flexShrink={0}
      >
        <Box minWidth={0}>
          <Typography component="h1" variant="subtitle1" fontWeight={600}>
            {state.view}
            {isTable || chartMeasures ? ' (use Data Split to breakdown)' : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
            Live reporting send dates {shownFrom} to {shownTo} · source as-of {formatSourceDate(report.sourceAsOf)}
          </Typography>
        </Box>
        <Stack direction="row" gap={0.5} flexShrink={0} flexWrap="wrap" alignItems="center">
          <PageChatButton />
          <Box component="span" title={csvAvailable ? undefined : csvUnavailableReason}>
            <Button
              size="small"
              disabled={!csvAvailable}
              title={csvAvailable ? undefined : csvUnavailableReason}
              onClick={downloadCsv}
            >
              {csvAvailable ? 'Download CSV' : 'CSV unavailable'}
            </Button>
          </Box>
          <Button size="small" onClick={() => setMethodOpen(true)}>
            Method and sources
          </Button>
        </Stack>
      </Stack>
      <Tabs
        value={state.view}
        onChange={(_, value) => update({ view: value })}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Original Tableau report views"
        sx={{ minHeight: 30, flexShrink: 0, '& .MuiTab-root': { minHeight: 30, px: 1.25, py: 0.5, fontSize: 11 } }}
      >
        {REPORT_VIEWS.map((view) => (
          <Tab key={view} value={view} label={view} />
        ))}
      </Tabs>
      <Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 3fr) minmax(140px, 1fr)' },
            gap: 1,
          }}
        >
          <FilterGroup title="Date and breakdown" columns={6}>
            <CompactControl
              label="Send date from"
              value={shownFrom}
              date
              onChange={(value) =>
                isWorksheetDateView(viewKey) ? update({ [SHEET_KEYS[viewKey].from]: value }) : filter('from', value)
              }
            />
            <CompactControl
              label="Send date to"
              value={shownTo}
              date
              onChange={(value) =>
                isWorksheetDateView(viewKey) ? update({ [SHEET_KEYS[viewKey].to]: value }) : filter('to', value)
              }
            />
            <CompactControl
              label="Date Cadence"
              value={state.cadence}
              values={CADENCES}
              onChange={(value) => update({ cadence: value as ReportState['cadence'] })}
            />
            <CompactControl
              label="Unique vs Total Metrics"
              value={state.filters.mode}
              values={['Unique', 'Total']}
              onChange={(value) => filter('mode', value)}
            />
            <CompactControl
              label="Data Split"
              value={state.split}
              values={SPLITS}
              onChange={(value) => update({ split: value as ReportState['split'] })}
            />
            <CompactControl
              label="Time Split"
              value={state.timeSplit}
              values={['Date Cadence', 'Full Period']}
              onChange={(value) => update({ timeSplit: value as ReportState['timeSplit'] })}
            />
          </FilterGroup>
          <FilterGroup title="Location" columns={1}>
            <MultiControl
              label="Country Group"
              value={state.filters.market}
              values={filterOptions.market}
              onApply={(value) => filter('market', value)}
            />
          </FilterGroup>
        </Box>
        <Box sx={{ mt: 0.75 }}>
          <FilterGroup title="UTMs" columns={2}>
            <MultiControl
              label="UTM Medium"
              value={state.filters.channel}
              values={filterOptions.channel}
              onApply={(value) => filter('channel', value)}
            />
            <MultiControl
              label="UTM Campaign"
              value={state.filters.campaign}
              values={filterOptions.campaign}
              onApply={(value) => filter('campaign', value)}
            />
          </FilterGroup>
        </Box>
        <Stack
          direction="row"
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
          gap={0.75}
          sx={{ mt: 1 }}
          aria-label="Active report scope"
        >
          {/* The worksheet tabs (Strategic Regions, Offer Clicks, Offer Vendor) query their own send dates. */}
          <Chip size="small" variant="outlined" label={`${shownFrom} to ${shownTo}`} sx={{ fontSize: 11 }} />
          <Chip
            size="small"
            variant="outlined"
            label={`${state.filters.mode} · ${state.cadence} · ${state.split}`}
            sx={{ fontSize: 11 }}
          />
          {(['channel', 'market', 'campaign'] as const)
            .filter((key) => selection(state.filters[key]).length > 0)
            .map((key) => {
              const selected = selection(state.filters[key]);
              const label = `${key === 'channel' ? 'UTM Medium' : key === 'market' ? 'Country Group' : 'UTM Campaign'}: ${selected.join(', ')}`;
              return (
                <Chip
                  key={key}
                  size="small"
                  variant="outlined"
                  label={label}
                  title={label}
                  onDelete={() => filter(key, 'All')}
                  sx={{ fontSize: 11, maxWidth: 280 }}
                />
              );
            })}
          {viewFilters
            .map(({ key, label }) => ({ key, label, values: viewFilterValues(params, viewKey, key) }))
            .filter(({ values }) => values.length > 0)
            .map(({ key, label, values }) => (
              <Chip
                key={key}
                size="small"
                variant="outlined"
                label={label + ': ' + values.join(', ')}
                title={label + ': ' + values.join(', ')}
                onDelete={() => viewFilter(key, 'All')}
                sx={{ fontSize: 11, maxWidth: 280 }}
              />
            ))}
          <Button
            size="small"
            onClick={() => {
              const next = new URLSearchParams(params);
              config.filters.forEach(({ id }) => next.delete(id));
              ALL_VIEW_FILTER_KEYS.forEach((key) => next.delete(key));
              for (const key of [
                'vendor_set',
                'offer_substring',
                'message_input',
                'vendor_mode',
                'pane_split',
                'pane_from',
                'pane_to',
                'grain',
                'data_split',
                'click_from',
                'click_to',
                'time_split',
                'report_today',
                'trend_from',
                'trend_to',
                'click_split',
              ]) {
                next.delete(key);
              }
              const reset = readReport(new URLSearchParams(), now);
              reportParams(reset).forEach((_, key) => next.delete(key));
              next.delete('view');
              setParams(next, { replace: true });
              setState(reset);
              setPage(0);
              setSort(null);
            }}
          >
            Reset filters
          </Button>
          <Button size="small" onClick={() => setUnavailableOpen((open) => !open)} aria-expanded={unavailableOpen}>
            {unavailableOpen ? 'Hide' : 'Show'} additional filters
          </Button>
        </Stack>
        <Collapse in={unavailableOpen}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', my: 0.75 }}>
            The quick filters Tableau shows on this tab, with Tableau's starting selections. Dropdown values come from
            the live source; type in the search box to add a value that is not listed.
          </Typography>
          <FilterGroup title="Additional filters" columns={6}>
            {viewFilters.map(({ label, key }) => (
              <MultiControl
                key={key}
                label={label}
                value={selectionValue(viewFilterValues(params, viewKey, key))}
                values={query.data?.filterOptions?.[key] ?? []}
                onApply={(value) => viewFilter(key, value)}
              />
            ))}
            {SEND_VIEWS.includes(viewKey) && (
              <CompactControl
                label="Offer Filter (literal substring)"
                value={params.get('offer_substring') ?? ''}
                onChange={(value) => nativeParameter('offer_substring', value)}
              />
            )}
          </FilterGroup>
        </Collapse>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block', mt: 0.75 }}>
          All normalises the original Null split label. Weekly buckets start Monday. Country Group and UTM selections
          change the report after Apply.
        </Typography>
      </Box>
      {!settled.ready && (
        <Alert
          severity={query.error ? 'error' : query.isFetching ? 'info' : 'warning'}
          action={
            query.error || cardFailed ? (
              <Button variant="text" size="small" onClick={() => void query.refetch()}>
                Retry
              </Button>
            ) : undefined
          }
        >
          {query.error?.message ?? settled.problem}
        </Alert>
      )}
      {viewKey === 'offers' && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
          Tableau opens this tab on every send date. Here it opens on the last 4 weeks and shows up to 42 days at a
          time, because over all dates the two offer worksheets hold about 6.9 million rows, more than the page can
          load.
        </Typography>
      )}
      {error && (
        <Alert severity="warning" sx={{ py: 0, fontSize: 11, flexShrink: 0 }}>
          {error}
        </Alert>
      )}
      {isTable && (
        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Typography component="h2" variant="body2" sx={{ py: 0.5 }}>
            Metrics
          </Typography>
          <TableContainer sx={{ maxHeight: 650, border: 1, borderColor: 'divider' }}>
            <Table
              stickyHeader
              size="small"
              aria-label="Customer Comms nineteen key metrics"
              sx={{
                minWidth: 2140,
                '& th, & td': {
                  px: 0.75,
                  py: 0.4,
                  fontSize: 10,
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                },
                '& thead th': { fontWeight: 600, height: 35 },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sort?.key === 'split' ? sort.direction : false}>
                    <TableSortLabel
                      active={sort?.key === 'split'}
                      direction={sort?.direction ?? 'asc'}
                      onClick={() => sortBy('split')}
                    >
                      Data Split
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sort?.key === 'time' ? sort.direction : false}>
                    <TableSortLabel
                      active={sort?.key === 'time'}
                      direction={sort?.direction ?? 'desc'}
                      onClick={() => sortBy('time')}
                    >
                      Time Split
                    </TableSortLabel>
                  </TableCell>
                  {MEASURES.map((measure) => (
                    <TableCell
                      key={measure.key}
                      align="right"
                      sortDirection={sort?.key === measure.key ? sort.direction : false}
                    >
                      <TableSortLabel
                        active={sort?.key === measure.key}
                        direction={sort?.direction ?? 'desc'}
                        onClick={() => sortBy(measure.key)}
                      >
                        {measure.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRow(
                  { id: 'grand-total', split: 'Grand Total', time: '', values: report.totals },
                  true,
                  report.ready && rows.length === 0,
                )}
                {shownRows.map((row) => tableRow(row))}
              </TableBody>
            </Table>
          </TableContainer>
          {report.ready && rows.length === 0 && (
            <Typography variant="body2" sx={{ fontSize: 12, py: 1 }}>
              No rows for these filters.
            </Typography>
          )}
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ minHeight: 30 }}>
            <Typography variant="caption" sx={{ fontSize: 10 }}>
              {rows.length ? currentPage * PAGE_SIZE + 1 : 0}–{Math.min((currentPage + 1) * PAGE_SIZE, rows.length)} of{' '}
              {rows.length.toLocaleString('en-AU')} rows · Grand Total includes every matching row · CSV includes all
              rows
            </Typography>
            <Stack direction="row">
              <Button size="small" disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>
                Previous
              </Button>
              <Button size="small" disabled={currentPage + 1 >= pages} onClick={() => setPage(currentPage + 1)}>
                Next
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}
      {chartMeasures && (
        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                md: chartMeasures.length === 1 ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))',
              },
              gap: 0.75,
            }}
          >
            {chartMeasures.map((measure) => (
              <MetricChart key={measure} report={report} measure={measure} />
            ))}
          </Box>
        </Box>
      )}
      {isClicks && (
        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: '190px 190px minmax(150px, 1fr)' },
              gap: 1,
              flexShrink: 0,
            }}
          >
            <CompactControl
              label="Send Date | Clicks By Date from"
              value={state.clickFrom}
              date
              onChange={(value) => update({ clickFrom: value })}
            />
            <CompactControl
              label="Send Date | Clicks By Date to"
              value={state.clickTo}
              date
              onChange={(value) => update({ clickTo: value })}
            />
            <CompactControl
              label="Product Name"
              value={state.product}
              values={productOptions}
              onChange={(value) => update({ product: value })}
            />
          </Box>
          <RecoveredWorksheets
            key={worksheetIdentity}
            settled={settled}
            identity={worksheetIdentity}
            onContextChange={setWorksheetContext}
            view="clicks"
            params={params}
            onParameter={nativeParameter}
          />
          {clickError && (
            <Alert severity="warning" sx={{ py: 0, fontSize: 11 }}>
              {clickError}
            </Alert>
          )}
          <ClickDateChart report={report} />
          <ProductMeasures report={report} />
        </Box>
      )}
      {!isTable && !chartMeasures && !isClicks && (
        <RecoveredWorksheets
          key={worksheetIdentity}
          settled={settled}
          identity={worksheetIdentity}
          onContextChange={setWorksheetContext}
          view={viewKey}
          params={params}
          onParameter={nativeParameter}
        />
      )}
      {isTable && settled.ready && !report.sourceRows.length && !error && (
        <Typography variant="caption" sx={{ flexShrink: 0 }}>
          No matching send-source rows. Missing measures remain Unknown.
        </Typography>
      )}
      <Dialog
        open={methodOpen}
        onClose={() => setMethodOpen(false)}
        maxWidth="md"
        fullWidth
        aria-labelledby="customer-comms-method-title"
      >
        <DialogTitle id="customer-comms-method-title">Method and sources</DialogTitle>
        <DialogContent dividers>
          <Stack gap={1.5}>
            <Typography variant="body2">
              Restores the pinned eight-tab presentation, nineteen metrics and original precision, dense filters, four
              Comms charts, four Financial charts, full-width Sessions / Clicks and stacked date × Product clicks.
              Additional worksheet roles consume the recovered complete live batches in the same view structure.
            </Typography>
            <Typography variant="body2">
              Send operands come from analytics.comms_reporting_send_level; same-offer click operands come from
              analytics.comms_reporting_click_level. Published summary sqlproxy physical lineage remains unavailable, so
              warehouse consistency alone does not prove deployed Tableau attribution lineage. Source as-of is the
              newest send date in the table the tab reads (send level for Tables and Charts, click level for the click
              tabs). The newest day can still be loading, so it may be a part day.
            </Typography>
            <Typography variant="body2">
              Unique/Total chooses additive source counters, not period-wide distinct people. Ratios use sums of
              matching operands; missing values/zero denominators stay Unknown and verified zero stays zero. Money is
              AUD. Weekly buckets begin Monday; Full Period reduces table operands without changing chart cadence.
            </Typography>
            <Typography variant="body2">
              Blank dimensions and Unknown remain distinct in results. Campaign + Journey is Campaign Name + " + " +
              Journey Name, as in Tableau, with spaces trimmed from the ends, so a blank Journey Name gives values such
              as "te_australia +". It is Unknown only when Campaign Name or Journey Name is missing. Filter values are
              matched with spaces trimmed from the ends. Platform starts on braze on Tables and Charts. Strategic
              Regions and Offer Vendor start on every send date, and Offer Vendor starts on Journey Name Unknown
              (Tableau's Null), TE Cadence Daily, Hero Tail Hero and Email Placement 1, 2, 3 and Hero 1 to 4, as in
              Tableau. Ordinary source filters, literal offer substring, independent worksheet dates, saved
              vendor/hero/cadence restrictions and all-field linked Offer selection use the recovered contracts. Custom
              Message input applies only to the recovered worksheet panes; the separate click-source product measures
              retain their declared source scope. No frozen extraction or fake catalogue is used.
            </Typography>
            <Typography variant="body2">
              Export and AskRichard reads require a complete settled response for this scope, including every batch
              count. Table pagination does not limit CSV. Final matched visual, warehouse/native comparison,
              interaction, actual download/hash and host acceptance are queued; current unit checks do not certify those
              steps.
            </Typography>
            <Button href={workbookUrl} target="_blank" rel="noreferrer" sx={{ alignSelf: 'start' }}>
              Open original workbook
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMethodOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default function CustomerCommsPage({ printTarget }: NativePrintProps) {
  return (
    <NativeReportShell
      pageId="customer-comms"
      title="Customer Comms Key Report"
      printTarget={printTarget}
      analytics
    >
      <NativeCustomerCommsContent />
    </NativeReportShell>
  );
}
